import { useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Badge, Grid } from "@mui/material";
import { useTheme } from "@mui/material";
import { darken, emphasize, lighten } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import { deepCopyObject, OmniStringToDate } from "../../utils/helpers";
import OmniAvatar from "../ui/omniAvatar";
import Widget from "./widget";

const PatientAgendaCalendar = props => {
  const { patientODM, patientChart } = props;

  const targetRefWidth = 585;

  const targetRef = useRef();

  const updateDisplay = () => {
    setDayOffset(targetRef.current.offsetWidth < targetRefWidth ? 2 : 3);
  };

  useEffect(() => {
    try {
      setDayOffset(targetRef.current.offsetWidth < targetRefWidth ? 2 : 3);
      window.addEventListener("resize", updateDisplay);
      return () => window.removeEventListener("resize", updateDisplay);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const [complianceData] = useState(
    patientChart && patientChart.treatmentPlans != null
      ? deepCopyObject(patientChart.treatmentPlans[0].compliance)
      : null,
  );

  const [dayOffset, setDayOffset] = useState(3);

  const [selectedDate] = useState(new Date());
  const newDate = new Date();
  newDate.setDate(newDate.getDate() + 7);

  const dayOffsetTime = 24 * 60 * 60 * 1000;

  const getScheduleLabel = date => {
    let oldestBoundsDate = OmniStringToDate(date);
    let newerBoundsDate = OmniStringToDate(date);
    newerBoundsDate.setTime(newerBoundsDate.getTime() + dayOffsetTime * 7);

    let theReturn = constants.monthNamesLong[oldestBoundsDate.getMonth()];

    // Get month text
    if (oldestBoundsDate.getMonth() !== newerBoundsDate.getMonth()) {
      theReturn =
        constants.monthNames[oldestBoundsDate.getMonth()] +
        " - " +
        constants.monthNames[newerBoundsDate.getMonth()];
    }

    if (oldestBoundsDate.getFullYear() !== newerBoundsDate.getFullYear()) {
      let theReturns = theReturn.split("-");
      theReturn =
        theReturns[0].toString() +
        " " +
        oldestBoundsDate.getFullYear() +
        " - " +
        theReturns[1].toString() +
        " " +
        newerBoundsDate.getFullYear();
    } else {
      theReturn += " " + oldestBoundsDate.getFullYear();
    }

    return theReturn;
  };

  const checkForComplianceOnDate = date => {
    if (complianceData == null) {
      return null;
    }

    try {
      let index = complianceData.length - 1;
      for (; index < complianceData.length; --index) {
        const stampDate = OmniStringToDate(complianceData[index].date);
        if (stampDate.toDateString() === date.toDateString()) {
          return complianceData[index].value !== 0;
        }
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const getDayToolTip = (index, compliance, isToday, isFuture) => {
    if (index === 0) {
      return "Medication Reporting and 2 Assessments";
    } else if (index === 4) {
      return "Independent Function Assessment";
    } else if (compliance == null) {
      if (isToday) {
        return "Click to Report";
      } else if (isFuture) {
        return "";
      } else {
        return "No Compliance Reported";
      }
    } else {
      if (compliance) {
        return "Followed Treatment Plan";
      } else {
        return "Non-Compliance";
      }
    }
  };

  const theme = useTheme();

  const getDayWidget = (date, index) => {
    var today = new Date();
    const isToday = today.toDateString() === date.toDateString();
    const compliance = checkForComplianceOnDate(date);
    const isFuture = Date.now() < date.getTime();

    return (
      <Badge
        key={"badge" + index}
        color="secondary"
        invisible={!(index === 0 || index === 4)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        overlap="circular"
        badgeContent={index === 0 ? 3 : 1}
        sx={{
          ".MuiBadge-badge": {
            pointerEvents: "none",
            background: theme.palette.highlight.main,
            color: theme.palette.highlight.contrastText,
            boxShadow: theme.shadows[3],
          },
        }}
      >
        <Tooltip
          title={getDayToolTip(index, compliance, isToday, isFuture)}
          placement="top"
          key={"avatarTips" + index}
        >
          <div>
            <OmniAvatar
              sx={[
                {
                  width: "48px",
                  height: "48px",
                  fontSize: "1.6rem",
                  marginRight: theme.spacing(0.5),
                  marginLeft: theme.spacing(0.5),
                },
                isToday && {
                  color: theme.palette.secondary.contrastText,
                  backgroundColor: theme.palette.secondary.main,
                  border: `2px solid ${theme.palette.secondary.main}`,
                  "&:hover": {
                    backgroundColor: emphasize(theme.palette.secondary.main, 0.25),
                    border: `2px solid ${emphasize(theme.palette.secondary.main, 0.25)}`,
                    // Reset on touch devices, it doesn't add specificity
                    "@media (hover: none)": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  },
                  boxShadow: theme.shadows[3],
                },
                !isToday &&
                  index !== 4 && {
                    color: darken(theme.palette.info.main, 0.5),
                    backgroundColor: lighten(theme.palette.info.main, 0.5),
                    border: `2px solid ${darken(theme.palette.info.main, 0.5)}`,
                    cursor: "default",
                  },
                !isToday &&
                  index === 4 && {
                    color: theme.palette.secondary.main,
                    backgroundColor: lighten(theme.palette.secondary.contrastText, 0.5),
                    border: `2px solid ${theme.palette.secondary.main}`,
                  },
              ]}
              key={"avatars" + index}
            >
              <div
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  marginTop: "-4px",
                }}
                key={"avatars" + index}
              >
                <Typography variant="overline">{constants.weekDayNames[date.getDay()]}</Typography>
                <Typography sx={{ textAlign: "center", marginTop: "-18px" }} variant="h5">
                  {date.getDate()}
                </Typography>
              </div>
            </OmniAvatar>
          </div>
        </Tooltip>
      </Badge>
    );
  };

  const getScheduleDays = date => {
    let oldestBoundsDate = OmniStringToDate(date);

    const days = [];
    for (let index = 0; index < 1 + 2 * dayOffset; ++index) {
      let aNewDay = OmniStringToDate(oldestBoundsDate);
      aNewDay.setTime(aNewDay.getTime() + dayOffsetTime * index);
      days.push(aNewDay);
    }
    return (
      <Grid container justifyContent={"space-around"}>
        {days.map((value, index) => {
          return <Grid item>{getDayWidget(value, index)}</Grid>;
        })}
      </Grid>
    );
  };

  if (patientODM == null || patientChart == null) {
    return <Widget>{/* add skeleton? */}</Widget>;
  }
  return (
    <div>
      <Grid container direction="row" justifyContent="space-between">
        <Typography
          variant="overline"
          sx={{
            webkitUserSelect: "none",
            userSelect: "none",
            marginTop: "-8px",
            marginBottom: "-4px",
          }}
          justify="flex-start"
        >
          7-Day Forecast
        </Typography>
        <Typography
          variant="overline"
          sx={{
            webkitUserSelect: "none",
            userSelect: "none",
            marginTop: "-8px",
            marginBottom: "-4px",
          }}
          justify="flex-end"
        >
          {getScheduleLabel(selectedDate)}
        </Typography>
      </Grid>
      {getScheduleDays(selectedDate)}
    </div>
  );
};

const mapStateToProps = state => ({
  patientODM: state.patientSession.patientODM,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PatientAgendaCalendar);
